"use client";

import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { analytics } from "@/lib/analytics";
import { useTurnstile } from "@/lib/use-turnstile";
import { API_TOPUP_PACKS, type ApiTopUpPack } from "@/lib/api-plans";

interface KeyRow {
  id: number;
  keyPrefix: string;
  plan: string;
  status: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  monthlyUsage: number;
  monthlyQuota: number | null;
}

const PLANS = [
  {
    id: "free" as const,
    name: "Free",
    price: "$0",
    features: ["50 units / month", "10 requests / minute", "Whisper fallback included", "No card required"],
  },
  {
    id: "starter" as const,
    name: "Starter",
    price: "$9",
    features: ["1,000 units / month", "30 requests / minute", "Whisper fallback included", "Cancel anytime"],
  },
  {
    id: "dev" as const,
    name: "Developer",
    price: "$29",
    features: ["10,000 units / month", "60 requests / minute", "Whisper fallback included", "Email support"],
    highlight: true,
  },
  {
    id: "scale" as const,
    name: "Scale",
    price: "$79",
    features: ["50,000 units / month", "300 requests / minute", "Whisper fallback included", "Priority support"],
  },
];

const PLAN_NAMES: Record<string, string> = { free: "Free", starter: "Starter", dev: "Developer", scale: "Scale" };

export function DevelopersClient() {
  const { containerId: turnstileContainerId, getToken: getTurnstileToken } = useTurnstile();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [keys, setKeys] = useState<KeyRow[] | null>(null);
  const [authState, setAuthState] = useState<"loading" | "anonymous" | "signed_in">("loading");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeEmail, setFreeEmail] = useState("");
  const [freeLinkSent, setFreeLinkSent] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const [topUpUnits, setTopUpUnits] = useState(0);
  const [topUpNotice, setTopUpNotice] = useState<"success" | "canceled" | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("verified") === "1") setJustVerified(true);
      const topup = params.get("topup");
      if (topup === "success" || topup === "canceled") setTopUpNotice(topup);
    }
  }, []);

  const loadKeys = useCallback(async () => {
    try {
      const res = await fetch("/api/keys");
      if (res.status === 401) {
        setAuthState("anonymous");
        return;
      }
      const data = await res.json();
      setKeys(data.keys || []);
      setTopUpUnits(typeof data.topUpUnits === "number" ? data.topUpUnits : 0);
      setAuthState("signed_in");
    } catch {
      setAuthState("anonymous");
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const buyTopUp = async (pack: ApiTopUpPack) => {
    setCheckoutLoading(pack);
    setError(null);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiTopUp: pack }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to start checkout");
      }
    } catch {
      setError("Failed to start checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const requestFreeKey = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const turnstileToken = await getTurnstileToken();
      const res = await fetch("/api/free-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(turnstileToken ? { "cf-turnstile-token": turnstileToken } : {}),
        },
        body: JSON.stringify({ email: freeEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setFreeLinkSent(true);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = async (plan: (typeof PLANS)[number]["id"]) => {
    if (plan === "free") {
      analytics.trackApiAccessClick();
      if (authState === "signed_in") {
        createKey();
      } else {
        document.getElementById("free-key-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
        document.getElementById("free-key-email")?.focus();
      }
      return;
    }
    setCheckoutLoading(plan);
    setError(null);
    analytics.trackApiAccessClick();
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiPlan: plan }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to start checkout");
      }
    } catch {
      setError("Failed to start checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const createKey = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data.key) {
        setNewKey(data.key);
        await loadKeys();
      } else {
        setError(data.error || "Failed to create key");
      }
    } catch {
      setError("Failed to create key");
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async (id: number) => {
    if (!confirm("Revoke this key? Requests using it will stop working immediately.")) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/keys?id=${id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Failed to revoke key");
      }
      await loadKeys();
    } catch {
      setError("Failed to revoke key");
    } finally {
      setBusy(false);
    }
  };

  const activeKeys = keys?.filter((k) => k.status === "active") ?? [];

  return (
    <div>
      {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
        <>
          <Script
            src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
            strategy="afterInteractive"
          />
          <div
            id={turnstileContainerId}
            style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }}
          />
        </>
      )}
      {/* Pricing */}
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Pricing</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white dark:bg-gray-800 rounded-xl p-6 border ${
              plan.highlight
                ? "border-purple-500 ring-1 ring-purple-500"
                : "border-gray-200 dark:border-gray-700"
            }`}
          >
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
              <div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
              </div>
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => startCheckout(plan.id)}
              disabled={checkoutLoading !== null}
              className={`w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 ${
                plan.highlight
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-200 text-white dark:text-gray-900"
              }`}
            >
              {checkoutLoading === plan.id
                ? "Redirecting..."
                : plan.id === "free"
                  ? "Get a free key"
                  : `Get ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      <p className="-mt-8 mb-12 text-sm text-gray-500 dark:text-gray-400">
        Need more without a subscription? Pay-as-you-go packs start at $10 for 1,000 units and never expire.
        Buy them from your dashboard below after signing in.
      </p>

      {/* Key management */}
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Your API keys</h2>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {newKey && (
        <div className="mb-4 p-4 rounded-lg bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800">
          <p className="text-sm font-medium text-purple-900 dark:text-purple-200 mb-2">
            Your new key — copy it now, it won&apos;t be shown again:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono bg-gray-900 text-sky-300 rounded px-3 py-2 break-all">
              {newKey}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(newKey)}
              className="px-3 py-2 text-xs font-medium bg-purple-600 hover:bg-purple-700 text-white rounded cursor-pointer"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {authState === "loading" && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-12">Checking your account&hellip;</p>
      )}

      {authState === "anonymous" && (
        <div
          id="free-key-form"
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-12 text-sm text-gray-600 dark:text-gray-300"
        >
          {freeLinkSent ? (
            <p>
              <strong className="text-gray-900 dark:text-white">Check your inbox.</strong>{" "}
              We&apos;ve sent a confirmation link to <strong>{freeEmail}</strong> — click it and
              you&apos;ll come straight back here to create your free key. The link expires in
              15 minutes.
            </p>
          ) : (
            <>
              <p className="font-medium text-gray-900 dark:text-white mb-1.5">
                Try the API free — no card required
              </p>
              <p className="mb-4">
                Confirm your email and get a key with 50 units a month. Enough to integrate
                and evaluate; upgrade whenever you need volume.
              </p>
              <form onSubmit={requestFreeKey} className="flex gap-2 flex-wrap">
                <input
                  id="free-key-email"
                  type="email"
                  required
                  value={freeEmail}
                  onChange={(e) => setFreeEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 min-w-[220px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg cursor-pointer"
                >
                  {busy ? "Sending…" : "Email me a key link"}
                </button>
              </form>
              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                Paid plan customers are signed in automatically after checkout. Already
                subscribed? Restore access from the{" "}
                <a href="/?restore=1" className="text-purple-600 dark:text-purple-400 hover:underline">
                  home page
                </a>{" "}
                using your purchase email, then return here.
              </p>
            </>
          )}
        </div>
      )}

      {authState === "signed_in" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-12">
          {justVerified && activeKeys.length === 0 && !newKey && (
            <p className="px-5 py-3 text-sm bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-b border-green-100 dark:border-green-900">
              Email confirmed — create your free key below.
            </p>
          )}
          {activeKeys.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
              No active keys. Create your free key below (50 units/month) — paid plans
              unlock more units and up to 5 keys.
            </p>
          )}
          {activeKeys.map((k) => (
            <div
              key={k.id}
              className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center justify-between gap-4"
            >
              <div>
                <div className="font-mono text-sm text-gray-900 dark:text-white">
                  {k.keyPrefix}&hellip;{" "}
                  <span className="ml-2 text-xs font-sans text-gray-500 dark:text-gray-400">
                    {k.label || "unlabeled"} · {PLAN_NAMES[k.plan] ?? k.plan}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {k.monthlyQuota
                    ? `${k.monthlyUsage.toLocaleString()} / ${k.monthlyQuota.toLocaleString()} units this month`
                    : `${k.monthlyUsage.toLocaleString()} units this month`}
                </div>
              </div>
              <button
                onClick={() => revokeKey(k.id)}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg cursor-pointer disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          ))}
          <div className="px-5 py-4 bg-gray-50 dark:bg-gray-900/40">
            <button
              onClick={createKey}
              disabled={busy || activeKeys.length >= 5}
              className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg cursor-pointer"
            >
              {busy ? "Working..." : "Create new key"}
            </button>
          </div>
        </div>
      )}

      {authState === "signed_in" && (
        <div id="top-up" className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-12">
          {topUpNotice === "success" && (
            <p className="mb-4 px-3 py-2 rounded-lg text-sm bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              Payment received. Your units appear below within a few seconds; refresh if they don&apos;t.
            </p>
          )}
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1.5">
            <h3 className="font-semibold text-gray-900 dark:text-white">Pay as you go</h3>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Top-up balance: <strong className="text-gray-900 dark:text-white">{topUpUnits.toLocaleString()} units</strong>
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            One-time packs that never expire. They&apos;re used only after your plan&apos;s monthly units run out,
            work on any plan (including Free), and are shared by all your keys.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {(Object.entries(API_TOPUP_PACKS) as [ApiTopUpPack, (typeof API_TOPUP_PACKS)[ApiTopUpPack]][]).map(([id, p]) => (
              <button
                key={id}
                onClick={() => buyTopUp(id)}
                disabled={checkoutLoading !== null}
                className="text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-400 disabled:opacity-50 cursor-pointer"
              >
                <div className="font-medium text-gray-900 dark:text-white">{p.label}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {checkoutLoading === id ? "Redirecting..." : `$${p.priceUsd} one-time`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
